import { SiteInfo } from '../views/CalendarMultiView'
import { GanttUser, GanttTask, Dependency, GanttMilestone, Partner, ScheduleDetailResponse, PendingChange } from '../types/gantt.types'
import { useState, useRef, useEffect } from 'react'
import { useHolidayRange } from './useHolidays'
import { addDays } from '../constants/gantt.logic'
import { holidayQueryRange, type HolidayGate } from '../views/scheduleUtils'
import { apiClient } from '@indipia/shared'
import { COLORS, EXTRA_PAD_DAYS, LEAD_PAD_DAYS } from '../constants/gantt.const'
// 간트 데이터(공종·선후관계·마일스톤·비고·경계) 상태 보관 + 조회 + 거울 ref — 이동 전 GanttView.tsx 본문을 그대로 옮긴 것이다.

export type UseGanttDataArgs = {
  selectedSite: SiteInfo | undefined
  user: GanttUser
}

export function useGanttData({ selectedSite, user }: UseGanttDataArgs) {
  const [tasks,          setTasks]          = useState<GanttTask[]>([])
  const [deps,           setDeps]           = useState<Dependency[]>([])
  const [loading,        setLoading]        = useState(false)
  const [loadError,      setLoadError]      = useState(false)
  const [pendingChanges, setPendingChanges] = useState<Map<number, PendingChange>>(new Map())

  // 드래그 중 시각 피드백용 — 실제 task.s는 변경하지 않음 (모바일과 동일)
  const [milestones,     setMilestones]     = useState<GanttMilestone[]>([])
  const [partners,        setPartners]        = useState<Partner[]>([])
  const [refreshKey,       setRefreshKey]       = useState(0)
  // 발주 정보 조회 상태(E-36) — 실패해도 공종·선후관계는 그대로 그리고 발주·입고 영역만 오류로 표시. 실패 값을 빈 목록으로 덮지 않는다.
  const [ordersStatus,     setOrdersStatus]     = useState<'loading' | 'ok' | 'error'>('loading')
  const [ordersRetryKey,   setOrdersRetryKey]   = useState(0)
  const orderScheduleIdRef = useRef<number | null>(null)
  const orderMapRef = useRef<Map<number, { orderDate: string | null; deliveryDate: string | null }>>(new Map())
  // 응답 도착 시점에도 같은 현장을 보고 있는지 확인 — 빠른 현장 전환 시 늦은 응답이 덮어쓰는 것을 방지
  const latestScheduleIdRef = useRef<number | null>(null)
  const [scheduleRemark,  setScheduleRemark]  = useState('')
  const [remarkDraft,     setRemarkDraft]     = useState('')
  const [ganttBounds,    setGanttBounds]    = useState<{ start: Date; end: Date }>({
    start: selectedSite?.startDate ? new Date(selectedSite.startDate) : new Date(),
    end:   selectedSite?.endDate   ? new Date(selectedSite.endDate)   : new Date(),
  })

  // 공휴일(BE `/schedule/holidays`) — 토·일과 함께 회색으로 표시(간트·PDF 공통).
  // 작업일 계산이 가시 범위 밖으로 나가도 휴일이 반영되도록 조회 범위는 현장 기간 시작 −1개월 ~ 종료 +1년(A-08).
  // 현장 기간이 없으면 조회하지 않는다 — 이때 status 는 idle 이고 날짜 이동·저장은 막힌다(빈 집합으로 대체하지 않음).
  const holidayRange = selectedSite?.startDate && selectedSite?.endDate
    ? holidayQueryRange(new Date(selectedSite.startDate), new Date(selectedSite.endDate))
    : null
  const holidayState = useHolidayRange(holidayRange?.from, holidayRange?.to)
  const holidays = holidayState.dates

  const [loadedScheduleId, setLoadedScheduleId] = useState<number | null>(null)
  const originalTasksRef   = useRef<GanttTask[]>([])
  const ganttBoundsRef     = useRef<{ start: Date; end: Date }>(ganttBounds)
  const tasksRef           = useRef<GanttTask[]>([])
  const depsRef            = useRef<Dependency[]>([])
  const pendingChangesRef  = useRef<Map<number, PendingChange>>(new Map())
  const scheduleEndRef     = useRef<Date | null>(null)
  const holidaysRef        = useRef<Set<string>>(new Set())
  // 공휴일 조회 상태·범위 거울 — 문서 단위 마우스 리스너([] 의존)가 계산 전에 확인한다(A-08)
  const holidayGateRef     = useRef<HolidayGate>({ status: 'idle', from: null, to: null })
  // 이동 가능 범위(A-11) — 로드·저장·취소 시점의 표시 기간(공종·마일스톤·현장 기간 전체) 앞 2주(간트 앞쪽 여유와 같음)·뒤 3일. 끌기·리사이즈로 넓어지지 않는다.
  const movableRangeRef    = useRef<{ start: Date; end: Date }>({ start: addDays(ganttBounds.start, -LEAD_PAD_DAYS), end: addDays(ganttBounds.end, EXTRA_PAD_DAYS) })
  const insertAfterIdRef   = useRef<number | null>(null)
  const prevTaskIdsRef     = useRef<Set<number>>(new Set())

  // barDragState: task.startDate는 드래그 중 불변 — offset만 추적
  const milestonesRef = useRef<GanttMilestone[]>([])
  const orderMetaRef   = useRef<Map<number, { vendorId: number | null; vendorName: string }>>(new Map())

  useEffect(() => { setScheduleRemark(''); setRemarkDraft('') }, [selectedSite?.scheduleId])

  // ── Fetch gantt (tasks + deps + milestones 단일 조회) ─────────
  useEffect(() => {
    if (!selectedSite?.scheduleId) { latestScheduleIdRef.current = null; setTasks([]); setDeps([]); setMilestones([]); return }
    const requestedId = selectedSite.scheduleId
    latestScheduleIdRef.current = requestedId
    const controller = new AbortController()
    setLoading(true)
    setLoadError(false)
    setPendingChanges(new Map())
    const headers = {
      ...(user?.compId   ? { 'X-Comp-Id': String(user.compId)   } : {}),
      ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
    }
    const fetchOnce = () => Promise.all([
      apiClient.get<{ data: ScheduleDetailResponse }>(
        `/schedule/schedules/${selectedSite.scheduleId}`, { headers, signal: controller.signal }
      ),
      apiClient.get<{ data: { milestones: GanttMilestone[] } }>(
        `/schedule/schedules/${selectedSite.scheduleId}/milestones`, { headers, signal: controller.signal }
      ),
    ])

    // 스케줄 생성/리셋 직후 첫 조회가 서버 반영 지연으로 실패하는 경우가 있어
    // 점점 늘려가며 최대 3회 자동 재시도 (새로고침 후 재클릭 시 성공하는 것과 동일한 효과).
    // 1회(800ms) 재시도만으로는 부족한 사례가 있어 백오프 폭을 늘렸다.
    const RETRY_DELAYS_MS = [800, 1600, 3200]
    const fetchWithRetry = async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          return await fetchOnce()
        } catch (err) {
          if (controller.signal.aborted || attempt >= RETRY_DELAYS_MS.length) throw err
          await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]))
          if (controller.signal.aborted) throw err
        }
      }
    }

    fetchWithRetry()
      .then(([detailRes, msRes]) => {
        if (latestScheduleIdRef.current !== requestedId) return
        const gd = detailRes.data.data

        // 발주·입고 일자는 별도 조회(아래 effect) 결과를 쓴다 — 실패해도 간트는 그대로 그린다(E-36 부분 오류)
        const orderMap = orderMapRef.current

        // 동일 공종(workTypeCode)은 같은 행에 여러 바로 묶이므로(groupTasksIntoRows) 색도
        // 같아야 한다 — 배열 인덱스가 아니라 workTypeCode 첫 등장 순서로 색을 배정한다.
        const workTypeColorMap = new Map<string, string>()
        ;(gd?.tasks ?? []).forEach(t => {
          if (!workTypeColorMap.has(t.workTypeCode)) {
            workTypeColorMap.set(t.workTypeCode, COLORS[workTypeColorMap.size % COLORS.length])
          }
        })
        const mappedTasks = (gd?.tasks ?? []).map((t, i) => ({
          taskId:         t.taskId,
          workTypeCode:   t.workTypeCode,
          workTypeName:   t.workTypeName,
          startDate:      new Date(t.startDate),
          endDate:        new Date(t.endDate),
          progress:       Number(t.progress) ?? 0,
          foremanName:    t.foremanName || null,
          foremanContact: t.foremanContact || null,
          description:    t.scheduleMemo ?? '',
          color:          workTypeColorMap.get(t.workTypeCode) ?? COLORS[i % COLORS.length],
          orderDate:      orderMap.get(t.taskId)?.orderDate ?? null,
          deliveryDate:   orderMap.get(t.taskId)?.deliveryDate ?? null,
          leadTime:       t.leadTime ?? 0,
          partnerStatus:  t.partnerStatus ?? '',
          respondedDtm:   t.respondedDtm ?? null,
          sentDtm:        t.sentDtm ?? null,
          assigneePartnerId: t.assigneePartnerId ?? null,
          assignSource:   t.assignSource ?? null,
          commYn:         t.commYn ?? null,
          confirmRequest: t.confirmRequest ?? null,
          requestCount:   t.requestCount ?? null,
          unsentCount:    t.unsentCount ?? null,
          appliedCount:   t.appliedCount ?? null,
          constructByVendor: t.constructByVendorYn === 'Y',
          // SIF-1 4-3: 서버가 task별 방식을 내려주면 그대로, 없으면 constructByVendorYn 로만 보정(VENDOR_CONSTRUCT 여부만 판별)
          orderMode: (t.orderMode as GanttTask['orderMode']) ?? (t.constructByVendorYn === 'Y' ? 'VENDOR_CONSTRUCT' : null),
          defaultOrderMode: (t.defaultOrderMode as GanttTask['defaultOrderMode']) ?? null,
        }))
        const mappedDeps = (gd?.dependencies ?? []).map(d => ({
          dependencyId: d.dependencyId, predecessorTaskId: d.predecessorTaskId,
          successorTaskId: d.successorTaskId, dependencyType: d.dependencyType,
        }))
        const mappedMs = (msRes.data.data?.milestones ?? []).map(m => ({
          milestoneId: m.milestoneId, milestoneType: m.milestoneType,
          milestoneName: m.milestoneName, milestoneDate: m.milestoneDate,
          milestoneDescription: m.milestoneDescription,
        }))

        const allStarts = mappedTasks.map(t => t.startDate.getTime())
        const allEnds   = mappedTasks.map(t => t.endDate.getTime())
        const msTimes   = mappedMs.map(m => new Date(m.milestoneDate).getTime())
        const siteTimes = [
          ...(selectedSite?.startDate ? [new Date(selectedSite.startDate).getTime()] : []),
          ...(selectedSite?.endDate   ? [new Date(selectedSite.endDate).getTime()]   : []),
        ]
        // 공시기간(스케줄 자체의 startDate/endDate)을 bounds에 포함 — selectedSite가 아직
        // 업데이트되지 않은 경우(생성 직후 등)에도 전체 공사 기간이 차트에 표시되도록
        const schedTimes = [
          ...(gd?.startDate ? [new Date(gd.startDate).getTime()] : []),
          ...(gd?.endDate   ? [new Date(gd.endDate).getTime()]   : []),
        ]
        const allTimes  = [...allStarts, ...allEnds, ...msTimes, ...siteTimes, ...schedTimes]
        const newBounds = allTimes.length ? {
          start: new Date(Math.min(...allTimes)),
          end:   new Date(Math.max(...allTimes)),
        } : { start: new Date(), end: new Date() }

        // "이 공종 뒤에 추가" 요청이 있으면 새 태스크를 해당 공종 바로 다음으로 재정렬
        let finalTasks = mappedTasks
        if (insertAfterIdRef.current !== null) {
          const afterId = insertAfterIdRef.current
          const prevIds = prevTaskIdsRef.current
          const newTask = mappedTasks.find(t => !prevIds.has(t.taskId))
          if (newTask) {
            const reordered = mappedTasks.filter(t => t.taskId !== newTask.taskId)
            const afterIdx = reordered.findIndex(t => t.taskId === afterId)
            if (afterIdx !== -1) {
              reordered.splice(afterIdx + 1, 0, newTask)
              finalTasks = reordered
            }
          }
          insertAfterIdRef.current = null
          prevTaskIdsRef.current = new Set()
        }
        setTasks(finalTasks)
        originalTasksRef.current = finalTasks
        setDeps(mappedDeps)
        setMilestones(mappedMs)
        setScheduleRemark(gd?.remark ?? '')
        setRemarkDraft(gd?.remark ?? '')
        ganttBoundsRef.current = newBounds
        movableRangeRef.current = { start: addDays(newBounds.start, -LEAD_PAD_DAYS), end: addDays(newBounds.end, EXTRA_PAD_DAYS) }
        setGanttBounds(newBounds)
      })
      .catch((err) => {
        if (latestScheduleIdRef.current !== requestedId) return
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return
        setTasks([]); setDeps([]); setMilestones([]); setLoadError(true)
      })
      .finally(() => {
        if (latestScheduleIdRef.current === requestedId) { setLoading(false); setLoadedScheduleId(requestedId) }
      })
    return () => controller.abort()
  }, [selectedSite?.scheduleId, user?.compId, user?.memberId, refreshKey])

  // ── Fetch orders (발주·입고 일자 · 업체) — 간트 본 조회와 분리, 실패는 부분 오류(E-36) ──────────
  useEffect(() => {
    if (!selectedSite?.scheduleId) { orderMapRef.current = new Map(); orderMetaRef.current.clear(); orderScheduleIdRef.current = null; setOrdersStatus('loading'); return }
    const requestedId = selectedSite.scheduleId
    // 현장이 바뀌면 이전 현장의 발주 정보를 비운다(같은 현장 재조회는 유지)
    if (orderScheduleIdRef.current !== requestedId) { orderScheduleIdRef.current = requestedId; orderMapRef.current = new Map(); orderMetaRef.current.clear() }
    const controller = new AbortController()
    setOrdersStatus('loading')
    const headers = {
      ...(user?.compId   ? { 'X-Comp-Id': String(user.compId)   } : {}),
      ...(user?.memberId ? { 'X-User-Id': String(user.memberId) } : {}),
    }
    type OrderRow = { taskId: number; orderDate: string | null; deliveryDate: string | null; vendorId?: number | null; vendorName?: string }
    const RETRY_DELAYS_MS = [800, 1600, 3200]
    const fetchWithRetry = async () => {
      for (let attempt = 0; ; attempt++) {
        try {
          return await apiClient.get<{ data: { orders: OrderRow[] } }>(
            `/schedule/schedules/${requestedId}/orders`, { headers, signal: controller.signal })
        } catch (err) {
          if (controller.signal.aborted || attempt >= RETRY_DELAYS_MS.length) throw err
          await new Promise(r => setTimeout(r, RETRY_DELAYS_MS[attempt]))
          if (controller.signal.aborted) throw err
        }
      }
    }
    const applyToTasks = (m: Map<number, { orderDate: string | null; deliveryDate: string | null }>) => {
      const merge = (t: GanttTask): GanttTask => ({ ...t, orderDate: m.get(t.taskId)?.orderDate ?? null, deliveryDate: m.get(t.taskId)?.deliveryDate ?? null })
      setTasks(prev => prev.map(merge))
      originalTasksRef.current = originalTasksRef.current.map(merge)
    }
    fetchWithRetry()
      .then(res => {
        if (latestScheduleIdRef.current !== requestedId) return
        const m = new Map<number, { orderDate: string | null; deliveryDate: string | null }>()
        orderMetaRef.current.clear()
        ;(res.data.data?.orders ?? []).forEach(o => {
          m.set(o.taskId, { orderDate: o.orderDate, deliveryDate: o.deliveryDate })
          orderMetaRef.current.set(o.taskId, { vendorId: o.vendorId ?? null, vendorName: o.vendorName ?? '' })
        })
        orderMapRef.current = m
        applyToTasks(m)
        setOrdersStatus('ok')
      })
      .catch((err) => {
        if (latestScheduleIdRef.current !== requestedId) return
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return
        // 실패 값을 빈 목록으로 덮지 않는다 — 마커는 그리지 않고(화면에서 숨김) 오류 상태만 남긴다
        orderMapRef.current = new Map()
        orderMetaRef.current.clear()
        applyToTasks(orderMapRef.current)
        setOrdersStatus('error')
      })
    return () => controller.abort()
  }, [selectedSite?.scheduleId, user?.compId, user?.memberId, refreshKey, ordersRetryKey])

  // ── Fetch partners ────────────────────────────────────────────
  const fetchPartners = () => {
    if (!user?.compId) return
    const hdrs = { 'X-Comp-Id': String(user.compId) }
    apiClient.get<{ data: Partner[] }>('/schedule/partners', { headers: hdrs })
      .then(res => setPartners(res.data.data ?? []))
      .catch(() => {})
  }

  useEffect(() => {
    fetchPartners()
  }, [user?.compId])

  // ── Refs 동기화 ───────────────────────────────────────────────
  ganttBoundsRef.current    = ganttBounds
  tasksRef.current          = tasks
  depsRef.current           = deps
  pendingChangesRef.current = pendingChanges
  milestonesRef.current     = milestones
  scheduleEndRef.current    = selectedSite?.endDate ? new Date(selectedSite.endDate) : null
  holidaysRef.current       = holidays
  holidayGateRef.current    = { status: holidayState.status, from: holidayState.from, to: holidayState.to }

  return {
    tasks, deps, tasksRef, depsRef, scheduleEndRef, setTasks, setGanttBounds, pendingChangesRef,
    originalTasksRef, setPendingChanges, holidaysRef, ganttBoundsRef, setDeps, milestonesRef, milestones,
    ganttBounds, scheduleRemark, holidays, pendingChanges, setRefreshKey, remarkDraft, setScheduleRemark,
    setMilestones, orderMetaRef, loadedScheduleId, loading, loadError, partners, setRemarkDraft,
    ordersStatus, retryOrders: () => setOrdersRetryKey(k => k + 1),
    fetchPartners, insertAfterIdRef, prevTaskIdsRef, holidayState, holidayGateRef, movableRangeRef,
  }
}
